/**
 * @component SettingsPanel
 * @description Settings layout redesign (UI only):
 * - /settings            -> overview grid of cards
 * - /settings/<section>  -> breadcrumb + 2-col layout (sticky info card + Outlet content)
 * - Adds top header bar including "Ereignis hinzufügen" menu like UserDashboard
 */

import { Card, Icon } from '@chakra-ui/react'
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { useMemo, useCallback } from 'react'
import {
  LuUsers,
  LuReceiptEuro,
  LuBellDot,
  LuChartColumnStacked,
  LuUserPen,
  LuFileText,
} from 'react-icons/lu'

import { useUserStore } from '@/stores/user.store'
import { SUPPORT_PORTAL_URL } from '@/config'
import { InvoiceSettingsNav } from './invoices/InvoiceSettingsNav'
import { SubscriptionSettingsNav } from './subscription/SubscriptionSettingsNav'
import { ProfileSettingsNav } from './profile/ProfileSettingsNav'
import { BankSettingsNav } from './BankSettingsNav'
import { EMPTY_ACTIVITY } from '@/common/activity.common'
import { SibPageShell } from '@/components/layout/SibPageShell'
import { Page } from '@/components/layout/Page'
import { getUserDisplayName } from '@/helper/common.helper'
import { ENVIRONMENT_BANNER_OFFSET } from '@/theme/recipes/environment-banner.recipe'
import { endUserSession } from '@/providers/auth/session'

type SettingsSection = {
  key: string
  label: string
  path: string
  description: string
  icon: any
  infoTitle: string
  infoText: string
}

const SECTIONS: SettingsSection[] = [
  {
    key: 'invoice',
    label: 'Rechnung',
    path: '/settings/invoice',
    description:
      'Legen Sie Rechnungsdaten, Rechnungsautomatik und Rechnungsnummernserie fest oder bearbeiten Sie Ihre Rechnungsvorlage.',
    icon: LuReceiptEuro,
    infoTitle: 'Info',
    infoText:
      'Verwalten Sie Ihre Rechnungs-Einstellungen. Dazu gehören z. B. Rechnungsdaten, Nummernkreis und Vorlagen.',
  },
  {
    key: 'bank',
    label: 'Auftrags- & Kontaktbuch',
    path: '/settings/bank',
    description:
      'Verwalten Sie Ihre Auftraggeber (Kunden), an die Sie Rechnungen adressieren und Ihr Kontaktbuch mit den Beratern.',
    icon: LuUsers,
    infoTitle: 'Info',
    infoText:
      'Gut gepflegte Auftraggeberdaten erleichtern die Abrechnung erheblich.Sobald Aktivitäten einem Auftraggeber zugeordnet sind, können Rechnungen schneller und mit weniger manueller Nacharbeit erstellt werden.Hinterlegen Sie Ansprechpartner, Rechnungsadresse und individuelle Leistungen mit Preisen pro Einheit. Besonders bei externen Auftraggebern sorgt eine saubere Datenbasis für Klarheit, korrekte Rechnungen und weniger Abstimmungsaufwand.',
  },
  {
    key: 'notifications',
    label: 'Benachrichtigungen',
    path: '/settings/notifications',
    description: 'Legen Sie fest, welche Form von Benachrichtigungen Sie gerne erhalten möchten.',
    icon: LuBellDot,
    infoTitle: 'Info',
    infoText:
      'Steuern Sie, wie und wo Sie Benachrichtigungen erhalten. Benachrichtigungen informieren Sie beispielsweise über den Status Ihrer Bestellungen.',
  },
  {
    key: 'orders',
    label: 'Auftragsvolumen',
    path: '/settings/orders',
    description:
      'Tragen Sie das Auftragsvolumen ein, welches Sie mit der Union Investment vereinbart haben.',
    icon: LuChartColumnStacked,
    infoTitle: 'Info',
    infoText:
      'Das Auftragsvolumen gilt ausschließlich für Aufträge mit der Union Investment. Die Höhe der jeweiligen Volumina legen Sie gemeinsam mit Ihrem Ansprechpartner bei der Union Investment fest.',
  },
  {
    key: 'profile',
    label: 'Nutzerdaten',
    path: '/settings/profile',
    description: 'Verwalten Sie Ihre Nutzerdaten wie E-Mail-Adresse oder Passwort.',
    icon: LuUserPen,
    infoTitle: 'Info',
    infoText:
      'Bitte verwenden Sie ein sicheres Kennwort und ändern Sie es in regelmäßigen Abständen. Ihre Zugangsdaten werden verschlüsselt übertragen und gemäß den geltenden Datenschutzbestimmungen verarbeitet.',
  },
  {
    key: 'contract',
    label: 'Abonnement',
    path: '/settings/subscription',
    description: 'Verwalten Sie Ihr Abonnement.',
    icon: LuFileText,
    infoTitle: 'Info',
    infoText: 'Hier verwalten Sie Ihr Abonnement und Vertragsdaten.',
  },
]

function normalizePath(pathname: string) {
  return pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname
}

function isSettingsOverview(pathname: string) {
  return normalizePath(pathname) === '/settings'
}

function sectionFromPath(pathname: string) {
  const p = normalizePath(pathname)
  return SECTIONS.find(s => p === s.path || p.startsWith(`${s.path}/`))
}

export function SettingsPanel() {
  const { user, profile } = useUserStore()
  const navigate = useNavigate()
  const { pathname } = useLocation()

  const displayName = getUserDisplayName(user, profile)
  const atOverview = useMemo(() => isSettingsOverview(pathname), [pathname])
  const activeSection = useMemo(() => sectionFromPath(pathname), [pathname])

  const dashboardPath = user?.role === 'admin' ? '/bo/dashboard' : '/app/user/dashboard'

  // --- "Ereignis hinzufügen" actions ---
  // Settings page doesn't have calendar/order refs, so we navigate to dashboard.
  // Optional: pass state so the dashboard could open the modal automatically.
  const handleNewOrder = useCallback(() => {
    navigate(dashboardPath, { state: { action: 'new-order' } })
  }, [navigate, dashboardPath])

  const handleAddActivity = useCallback(() => {
    navigate('/activities/new', {
      state: {
        values: {
          ...EMPTY_ACTIVITY,
          date: new Date(),
        },
      },
    })
  }, [navigate])

  const handleCreateInvoice = useCallback(() => {
    navigate(dashboardPath, { state: { action: 'create-invoice' } })
  }, [navigate, dashboardPath])
  const handleLogout = async () => {
    // Central teardown (SIB-924): server logout + full client session reset,
    // including the user store. Local cleanup happens even if the server call fails.
    await endUserSession()
    navigate('/login', { replace: true })
  }

  return (
    <SibPageShell
      userName={displayName}
      onNewOrder={handleNewOrder}
      onAddActivity={handleAddActivity}
      onCreateInvoice={handleCreateInvoice}
      onGoDashboard={() => navigate(dashboardPath)}
      onOpenSupport={() => window.open(SUPPORT_PORTAL_URL, '_blank')}
      onLogout={handleLogout}
    >
      <Page.Root id="settings-root" data-testid="settings-panel">
        <Page.Header
          breadcrumbs={
            atOverview
              ? [{ label: 'Dashboard', to: dashboardPath }, { label: 'Einstellungen' }]
              : [
                  { label: 'Dashboard', to: dashboardPath },
                  { label: 'Einstellungen', to: '/settings' },
                  { label: activeSection?.label ?? 'Einstellungen' },
                ]
          }
          backLabel={atOverview ? 'Zum Dashboard' : 'Zu den Einstellungen'}
          onBack={() => navigate(atOverview ? dashboardPath : '/settings')}
          title={atOverview ? 'Einstellungen' : (activeSection?.label ?? 'Einstellungen')}
          meta={atOverview ? undefined : activeSection?.description}
        />

        <Page.Content>
          {atOverview ? (
            <Page.OverviewGrid>
              {SECTIONS.map(section => {
                const onboardingId =
                  section.key === 'invoice'
                    ? 'settings-tab-invoice'
                    : section.key === 'orders'
                      ? 'settings-tab-orders'
                      : section.key === 'bank'
                        ? 'settings-tab-bank'
                        : section.key === 'profile'
                          ? 'settings-tab-profile'
                          : section.key === 'contract'
                            ? 'settings-tab-contract'
                            : section.key === 'notifications'
                              ? 'settings-tab-notifications'
                              : undefined

                return (
                  <Link key={section.key} to={section.path}>
                    <Card.Root id={onboardingId} minH="260px">
                      <Card.Header>
                        <Icon as={section.icon} />
                        <Card.Title>{section.label}</Card.Title>
                      </Card.Header>
                      <Card.Body>
                        <Card.Description>{section.description}</Card.Description>
                      </Card.Body>
                    </Card.Root>
                  </Link>
                )
              })}
            </Page.OverviewGrid>
          ) : (
            <Page.NavigationColumns>
              <Page.Navigation>
                {activeSection?.key === 'invoice' ? (
                  <InvoiceSettingsNav />
                ) : activeSection?.key === 'bank' ? (
                  <BankSettingsNav />
                ) : activeSection?.key === 'contract' ? (
                  <SubscriptionSettingsNav />
                ) : activeSection?.key === 'profile' ? (
                  <ProfileSettingsNav />
                ) : (
                  <Card.Root
                    position={{ base: 'static', lg: 'sticky' }}
                    top={{ lg: `calc(${ENVIRONMENT_BANNER_OFFSET} + {spacing.24})` }}
                    width="80%"
                  >
                    <Card.Header>
                      <Card.Title>{activeSection?.infoTitle ?? 'Info'}</Card.Title>
                    </Card.Header>
                    <Card.Body>
                      <Card.Description>{activeSection?.infoText ?? ''}</Card.Description>
                    </Card.Body>
                  </Card.Root>
                )}
              </Page.Navigation>

              <Page.Main data-testid="settings-outlet">
                <Outlet />
              </Page.Main>
            </Page.NavigationColumns>
          )}
        </Page.Content>
      </Page.Root>
    </SibPageShell>
  )
}
